/**
 * Preview and embed helpers for the widget hub. The customer embed is
 * key-based: the loader fetches the saved config from the public widget API, so
 * nothing here carries styling. The website URL helpers are session-only.
 */

export function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Host-page trigger for the hub's per-assistant embed code. The button belongs
 * to the customer's own layout, so this markup carries no styling — only the
 * attribute the loader binds to, plus the venue for a picker template.
 */
export function createTriggerSnippet(venueId: string | null): string {
  const venueAttribute = venueId ? ` data-bonnie-venue="${escapeHtmlAttribute(venueId)}"` : ''

  return `<button type="button" data-bonnie-widget-open${venueAttribute}>
  Whatsapp chat
</button>`
}

/**
 * Turns the preview-only website field into an iframe src. Empty or unsafe
 * values stay on the blank mock. This URL is never persisted — templates are
 * shared, so a preview site belongs to the current editor session only.
 */
export function resolvePreviewWebsiteUrl(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null

  const withProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed) ? trimmed : `https://${trimmed}`

  let url: URL
  try {
    url = new URL(withProtocol)
  } catch {
    return null
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null
  }

  if (!url.hostname.includes('.') && url.hostname !== 'localhost') {
    return null
  }

  return `${url.origin}${url.pathname}${url.search}${url.hash}`
}

export function previewWebsiteHostname(src: string): string {
  try {
    return new URL(src).hostname
  } catch {
    return src
  }
}

export type PreviewDevice = 'desktop' | 'mobile'

/** How big the mock window is, in dashboard pixels. */
export const PREVIEW_FRAME_BODIES: Record<PreviewDevice, { width: number; height: number }> = {
  desktop: { width: 480, height: 301 },
  mobile: { width: 277, height: 600 },
}

/** Viewport the framed site should use, so desktop CSS actually kicks in. */
export const PREVIEW_SITE_WIDTHS: Record<PreviewDevice, number> = {
  desktop: 1280,
  mobile: 390,
}

/**
 * Size and scale for the live-site iframe. The site lays out at a real
 * desktop or phone width, then shrinks to fit the mock so the launcher
 * lands where it would on the customer's page.
 */
export function previewSiteWorld(device: PreviewDevice): {
  width: number
  height: number
  scale: number
} {
  const frame = PREVIEW_FRAME_BODIES[device]
  const width = PREVIEW_SITE_WIDTHS[device]
  const scale = frame.width / width

  return {
    width,
    height: Math.round(frame.height / scale),
    scale,
  }
}
